import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getCompanyName } from '../lib/tenant';
import { useAuth } from '../components/AuthContext';
import { sendNotification, getAdminIds, getBranchManagerIds, getAdminEmails, getBranchManagerEmails, sendEmail, emailSpareRequestSubmitted, emailToolShipped, emailInspectionDiscrepancy } from '../lib/notify';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';
import MobileInspection from './MobileInspection';
import { calcSavings, calcAcquisitionSavings, fmt$, resolveToolValue } from '../lib/utils';
import OnboardingCallout from '../components/OnboardingCallout';
import { isBranchScopedManager } from '../lib/permissions';

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const displayLabel = selected.length === 0 ? label : selected.length === 1 ? options.find(o => o.value === selected[0])?.label || selected[0] : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="filter-select"
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', cursor: 'pointer', background: 'white', minWidth: '140px' }}
      >
        <span style={{ color: selected.length === 0 ? 'var(--gray-mid)' : 'var(--black)' }}>{displayLabel}</span>
        <span style={{ fontSize: '10px', color: 'var(--gray-lt)' }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '180px', padding: '4px 0',
        }}>
          {selected.length > 0 && (
            <button onClick={() => onChange([])} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '12px', color: 'var(--green-dk)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
              Clear all
            </button>
          )}
          {options.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: 'var(--black)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--off-white)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} style={{ accentColor: 'var(--green-dk)' }} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
const STATUS_BADGE = {
  active: 'badge-active', tagged_out: 'badge-tagged', in_repair: 'badge-repair',
  in_transit: 'badge-transit', retired: 'badge-retired', spare: 'badge-spare', pending: 'badge-pending',
};

// ── REQUESTS PAGE ──────────────────────────────────────────
export function RequestsPage() {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({});
  const [spares, setSpares] = useState([]);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [spareSearch, setSpareSearch] = useState('');
  const [branchSpares, setBranchSpares] = useState([]);
  const [assignConfirm, setAssignConfirm] = useState(null);
  const [replenishPrompt, setReplenishPrompt] = useState(null);
  const [noToolIdConfirm, setNoToolIdConfirm] = useState(null); // spare_request pending receipt with no linked tool_id — Issue #63
  const [approveModal, setApproveModal] = useState(null); // the spare_request being approved
  const [approvePool, setApprovePool] = useState([]);
  const [approveSearch, setApproveSearch] = useState('');
  const [approveToolId, setApproveToolId] = useState('');
  const [approveSaving, setApproveSaving] = useState(false);
  const sendMsg = useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('spare_requests').select('*').order('submitted_at', { ascending: false });
    if (!isAdmin) query = query.eq('requesting_branch_id', profile?.branch_id);
    const { data } = await query;
    setRequests(data || []);
    if (isBranchScoped && profile?.branch_id) {
      const { data: spareData } = await supabase
        .from('tools')
        .select('*')
        .eq('branch_id', profile.branch_id)
        .eq('is_branch_spare', true)
        .eq('status', 'active');
      setBranchSpares(spareData || []);
    }
    setLoading(false);
  }, [isBranchScoped, profile?.branch_id]);

  useEffect(() => { load(); }, [load]);

  async function openSendModal() {
  const { data: sp } = await supabase.from('tools').select('*').eq('is_spare', true).eq('status', 'available').order('name');
  const { data: br } = await supabase.from('branches').select('*').order('name');
  setSpares(sp || []);
  setBranches((br || []).filter(b => !b.is_headquarters));
  setSendForm({ branch_id: '', tool_id: '', notes: '' });
  setSpareSearch('');
  sendMsg.clearMessage();
  setShowSendModal(true);
}

  async function saveSend() {
    if (!sendForm.branch_id) { sendMsg.showError('Select a destination branch.'); return; }
    if (!sendForm.tool_id) { sendMsg.showError('Select a tool to send.'); return; }
    setSaving(true);
    const tool = spares.find(t => String(t.id) === String(sendForm.tool_id));
    const branch = branches.find(b => String(b.id) === String(sendForm.branch_id));
    await supabase.from('tools').update({ status: 'reserved' }).eq('id', tool.id);
    await supabase.from('spare_requests').insert({
      tool_id: tool.id,
      tool_name: tool.name,
      model_number: tool.model_number || null,
      serial_number: tool.serial_number || null,
      quantity: 1,
      manager_name: 'Admin — direct send',
      requested_by: profile?.id,
      requesting_branch_id: parseInt(sendForm.branch_id),
      requesting_branch_name: branch?.name || '',
      notes: sendForm.notes || `Sent directly by ${profile?.full_name}`,
      status: 'shipped',
      submitted_at: new Date().toISOString(),
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
      shipped_at: new Date().toISOString(),
      shipped_by_name: profile?.full_name,
    });
    const managerIds = await getBranchManagerIds(parseInt(sendForm.branch_id));
    await sendNotification(managerIds,
          `Tool incoming: ${tool.name}`,
          `${profile?.full_name} is sending a ${tool.name} to your branch. Confirm receipt when it arrives.`,
          'order_shipped',
          'tools',
          'fleet'
        );
    setSaving(false);
    setShowSendModal(false);
    load();
  }

  const filtered = requests.filter(r => !filter || r.status === filter);

  async function markShipped(id) {
    const req = requests.find(r => r.id === id);
    await supabase.from('spare_requests').update({
      status: 'shipped',
      shipped_at: new Date().toISOString(),
      shipped_by_name: profile?.full_name,
    }).eq('id', id);
    if (req?.requesting_branch_id) {
      const managerIds = await getBranchManagerIds(req.requesting_branch_id);
      await sendNotification(managerIds,
            `Tool shipped: ${req.tool_name}`,
            `Your request for ${req.tool_name} has been shipped. Confirm receipt when it arrives.`,
            'order_shipped',
            'tools',
            // CORRECTED at Session 78v, found while giving this email a
            // destination for #126 / M3. This said 'fleet', which opens Fleet
            // Inventory - but the manager's job here is to press Confirm
            // receipt, and that button is on the Tool Requests page
            // (RequestsPage, this file, page key 'requests'). The bell has
            // been sending them to a screen the action is not on. The email's
            // own hint text said 'Spare Requests' all along and was right.
            'requests',
            id
          );
      const managerEmails = await getBranchManagerEmails(req.requesting_branch_id);
      await sendEmail(
        managerEmails,
        `Tool shipped: ${req.tool_name}`,
        emailToolShipped(req.tool_name, req.requesting_branch_name, profile?.full_name, id)
      );
    }
    load();
  }

  async function openApproveModal(req) {
    const { data: pool } = await supabase
      .from('tools')
      .select('*')
      .eq('is_spare', true)
      .or(
        req.tool_id
          ? `status.eq.available,id.eq.${req.tool_id}`
          : `status.eq.available`
      )
      .order('name');
    const filtered = (pool || []).filter(t =>
      t.id === req.tool_id ||
      t.name?.toLowerCase().includes((req.tool_name || '').toLowerCase())
    );
    setApprovePool(filtered);
    setApproveSearch(req.tool_name || '');
    setApproveToolId(req.tool_id ? String(req.tool_id) : '');
    setApproveModal(req);
  }

  async function confirmApprove() {
    if (!approveToolId) return;
    setApproveSaving(true);
    const tool = approvePool.find(t => String(t.id) === String(approveToolId));
    // Update the request with the assigned tool details
    await supabase.from('spare_requests').update({
      status: 'approved',
      tool_id: tool.id,
      model_number: tool.model_number || null,
      serial_number: tool.serial_number || null,
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
    }).eq('id', approveModal.id);
    // Reserve the tool so it can't be double-assigned
    await supabase.from('tools').update({ status: 'reserved' }).eq('id', tool.id);
    // Notify the requesting manager
    if (approveModal.requested_by) {
      await sendNotification([approveModal.requested_by],
            `Request approved: ${approveModal.tool_name}`,
            `Your spare request for ${approveModal.tool_name} has been approved and will be shipped soon.`,
            'request_approved',
            'tools',
            'requests',
            approveModal.id
          );
    }
    setApproveSaving(false);
    setApproveModal(null);
    load();
  }

  async function markDenied(id) {
    const req = requests.find(r => r.id === id);
    const { data: reqData } = await supabase.from('spare_requests').select('tool_id').eq('id', id).maybeSingle();
    await supabase.from('spare_requests').update({
      status: 'denied',
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
    }).eq('id', id);
    if (reqData?.tool_id) {
      await supabase.from('tools').update({ status: 'spare' }).eq('id', reqData.tool_id);
    }
    if (req?.requested_by) {
      await sendNotification([req.requested_by],
            `Request denied: ${req.tool_name}`,
            `Your spare request for ${req.tool_name} was denied. Contact your administrator for more information.`,
            'request_rejected',
            'tools',
            'requests'
          );
    }
    load();
  }

  async function assignBranchSpare(r, spare) {
    // Assign spare to the requesting station
    await supabase.from('tools').update({
      station_truck: r.station_truck || null,
      is_branch_spare: false,
    }).eq('id', spare.id);
    // Log to tool_history
    await supabase.from('tool_history').insert({
      tool_id: spare.id,
      changed_by: profile?.id,
      change_type: 'branch_spare_assigned',
      notes: `Assigned as replacement for tagged-out ${r.tool_name} at ${r.station_truck || 'station'}. Spare request #${r.id}.`,
    });
    // Mark request fulfilled
    await supabase.from('spare_requests').update({
      status: 'fulfilled',
      fulfilled_at: new Date().toISOString(),
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
    }).eq('id', r.id);
    // Notify admin
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Branch spare assigned: ${spare.name}`,
            `${profile?.full_name} assigned a branch spare ${spare.name} to ${r.requesting_branch_name}. A replenishment request may follow.`,
            'receipt_confirmed',
            'tools',
            'fleet'
          );
    setAssignConfirm(null);
    setReplenishPrompt(r);
    load();
  }

  async function requestCentralPool(r) {
    await supabase.from('spare_requests').update({
      status: 'submitted',
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
    }).eq('id', r.id);
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Spare request submitted: ${r.tool_name}`,
            `${r.requesting_branch_name} needs a ${r.tool_name}. No branch spare was available.`,
            'spare_request_created',
            'tools',
            'requests'
          );
    load();
  }

  async function submitReplenishRequest(r) {
    await supabase.from('spare_requests').insert({
      tool_name: r.tool_name,
      quantity: 1,
      manager_name: profile?.full_name,
      requested_by: profile?.id,
      requesting_branch_id: r.requesting_branch_id,
      requesting_branch_name: r.requesting_branch_name,
      notes: `Replenishment request — branch spare was used to cover tagged-out ${r.tool_name}.`,
      status: 'submitted',
      submitted_at: new Date().toISOString(),
      reviewed_by_name: profile?.full_name,
      reviewed_at: new Date().toISOString(),
    });
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Replenishment needed: ${r.tool_name}`,
            `${r.requesting_branch_name} used their branch spare and needs a replacement sent from the central pool.`,
            'restock_needed',
            'tools',
            'requests'
          );
    setReplenishPrompt(null);
    load();
  }

  async function adminForceConfirm(r) {
    await confirmReceipt(r);
  }

  async function confirmReceipt(r) {
    // Issue #63 — a request with no linked tool_id is a legacy direct-send
    // record from before saveSend() reserved a real tool row on send. Every
    // current write path (saveSend, confirmApprove) guarantees tool_id is
    // set, so this can only happen against a pre-existing legacy row. Auto-
    // inserting a brand-new tools row here risked a silent duplicate if a
    // tools record for the same physical tool already existed elsewhere —
    // require an explicit admin decision instead of guessing.
    if (!r.tool_id) {
      setNoToolIdConfirm(r);
      return;
    }
    await doConfirmReceipt(r);
  }

  async function doConfirmReceipt(r) {
    await supabase.from('spare_requests').update({
      status: 'fulfilled',
      received_at: new Date().toISOString(),
      fulfilled_at: new Date().toISOString(),
    }).eq('id', r.id);
    if (r.tool_id) {
      // Update the existing reserved tool rather than creating a ghost record
      await supabase.from('tools').update({
        status: 'active',
        is_spare: false,
        is_branch_spare: true,
        branch_id: r.requesting_branch_id,
        description: `Received by ${r.requesting_branch_name} on ${new Date().toLocaleDateString()}`,
      }).eq('id', r.tool_id);
    } else {
      // Legacy direct-send record with no tool_id — only reached after
      // explicit admin confirmation via noToolIdConfirm (see confirmReceipt above).
      await supabase.from('tools').insert({
        name: r.tool_name,
        status: 'active',
        is_spare: false,
        is_branch_spare: true,
        branch_id: r.requesting_branch_id,
        model_number: r.model_number || null,
        serial_number: r.serial_number || null,
        description: `Received from central pool on ${new Date().toLocaleDateString()}`,
        entered_by: profile?.id,
      });
    }
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Receipt confirmed: ${r.tool_name}`,
            `${profile?.full_name} confirmed receipt of ${r.tool_name} at ${r.requesting_branch_name}.`,
            'receipt_confirmed',
            'tools',
            'fleet'
          );
    load();
  }

  const BADGE = {
    pending: 'badge-pending', submitted: 'badge-atshop', approved: 'badge-approved',
    shipped: 'badge-intransit', denied: 'badge-denied', fulfilled: 'badge-fulfilled',
  };
  const fmt = (ts) => ts ? new Date(ts).toLocaleDateString() : '—';
  const sf = (k, v) => setSendForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Spare requests</div>
        <div className="page-sub">{isAdmin ? 'Requests from branches for Dallas central spare pool tools' : 'Your branch spare requests'}</div>
      </div>
      <div className="toolbar">
        <select className="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="shipped">Shipped</option>
          <option value="denied">Denied</option>
          <option value="fulfilled">Fulfilled</option>
        </select>
        <span style={{ fontSize: '12px', color: 'var(--gray-lt)', marginLeft: '8px' }}>
          {requests.filter(r => r.status === 'pending').length} pending
          {requests.filter(r => r.status === 'submitted').length > 0 && ` · ${requests.filter(r => r.status === 'submitted').length} submitted`}
          {requests.filter(r => r.status === 'shipped').length > 0 && ` · ${requests.filter(r => r.status === 'shipped').length} in transit`}
        </span>
        {isAdmin && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openSendModal}>
            + Send tool to branch
          </button>
        )}
      </div>

      {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.length === 0 && (
            <div className="card"><div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No requests</div><div className="empty-state-sub">No spare requests match the current filter.</div></div></div>
          )}
          {filtered.map(r => (
            <div key={r.id} className="card">
              <div className="card-body">
                <div className="flex-center gap-2" style={{ marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-head)', fontSize: '15px', fontWeight: 700 }}>
                      {r.tool_name} <span style={{ fontWeight: 400, fontSize: '13px', color: 'var(--gray-mid)' }}>×{r.quantity || 1}</span>
                    </div>
                  </div>
                  <span className={`badge ${BADGE[r.status] || 'badge-pending'}`}>{r.status}</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--gray-mid)', marginBottom: '6px' }}>
                  <span>Branch: <strong style={{ color: 'var(--black)' }}>{r.requesting_branch_name || '—'}</strong></span>
                  <span>Manager: <strong style={{ color: 'var(--black)' }}>{r.manager_name}</strong></span>
                  <span>Submitted: <strong style={{ color: 'var(--black)' }}>{fmt(r.submitted_at)}</strong></span>
                  {r.date_needed && <span>Needed by: <strong style={{ color: 'var(--black)' }}>{fmt(r.date_needed)}</strong></span>}
                  {r.model_number && <span>Model: <strong style={{ color: 'var(--black)' }}>{r.model_number}</strong></span>}
                  {r.serial_number && <span>Serial: <strong style={{ color: 'var(--black)' }}>{r.serial_number}</strong></span>}
                  {r.shipped_at && <span>Shipped: <strong style={{ color: 'var(--black)' }}>{fmt(r.shipped_at)}</strong>{r.shipped_by_name && ` by ${r.shipped_by_name}`}</span>}
                  {r.received_at && <span>Received: <strong style={{ color: 'var(--black)' }}>{fmt(r.received_at)}</strong></span>}
                </div>
                {r.notes && <div style={{ fontSize: '12px', color: 'var(--gray-mid)', fontStyle: 'italic', marginBottom: '8px' }}>"{r.notes}"</div>}
                {isAdmin && (r.status === 'pending' || r.status === 'submitted') && (
                  <div className="flex gap-1">
                    <button className="btn btn-primary btn-sm" onClick={() => openApproveModal(r)}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => markDenied(r.id)}>Deny</button>
                  </div>
                )}
                {isAdmin && r.status === 'approved' && (
                  <button className="btn btn-primary btn-sm" onClick={() => markShipped(r.id)}>Mark as shipped</button>
                )}
                {isBranchScoped && r.status === 'shipped' && (
                  <button className="btn btn-primary btn-sm" onClick={() => confirmReceipt(r)}>Confirm receipt</button>
                )}
                {isAdmin && r.status === 'shipped' && (
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: '8px' }} onClick={() => adminForceConfirm(r)}>Force confirm receipt</button>
                )}
                {isBranchScoped && r.status === 'pending' && (() => {
                  const matchingSpare = branchSpares.find(s =>
                    s.name.toLowerCase() === r.tool_name.toLowerCase()
                  );
                  return (
                    <div style={{ marginTop: '8px' }}>
                      {matchingSpare ? (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--green-dk)', fontWeight: 600, marginBottom: '6px' }}>
                            ✓ Branch spare available: {matchingSpare.name}
                            {matchingSpare.model_number ? ` — ${matchingSpare.model_number}` : ''}
                            {matchingSpare.serial_number ? ` (S/N: ${matchingSpare.serial_number})` : ''}
                          </div>
                          <div className="flex gap-1">
                            <button className="btn btn-primary btn-sm" onClick={() => setAssignConfirm({ request: r, spare: matchingSpare })}>
                              Assign branch spare
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => requestCentralPool(r)}>
                              Request from central pool
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--amber, #E65100)', fontWeight: 600, marginBottom: '6px' }}>
                            ⚠ No branch spare available for {r.tool_name}
                          </div>
                          <button className="btn btn-primary btn-sm" onClick={() => requestCentralPool(r)}>
                            Request from central pool
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SEND TOOL MODAL ── */}
      {showSendModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowSendModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Send tool to branch</span>
              <button className="close-btn" onClick={() => setShowSendModal(false)}>×</button>
            </div>
            <div style={{ background: '#F0F4FF', padding: '0.75rem 1.25rem', borderBottom: '1px solid #C7D2FE', fontSize: '12px', color: '#3730A3' }}>
              This bypasses the request process. The tool will be marked as shipped immediately and the branch manager will be notified to confirm receipt.
            </div>
            <div className="modal-body">
             <InlineMessage type={sendMsg.type} message={sendMsg.message} onDismiss={sendMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group full">
                  <label className="form-label">Destination branch *</label>
                  <select className="form-select" value={sendForm.branch_id} onChange={e => sf('branch_id', e.target.value)}>
                    <option value="">Select branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
  <label className="form-label">Tool to send *</label>

  {/* Search input */}
  <input
    className="form-input"
    placeholder="Search spare pool..."
    value={spareSearch}
    onChange={e => setSpareSearch(e.target.value)}
    style={{ marginBottom: '8px' }}
  />

  {/* Scrollable tool list */}
  <div style={{
    border: '1px solid #D0D0D0',
    borderRadius: 'var(--radius)',
    maxHeight: '220px',
    overflowY: 'auto',
    background: 'white',
  }}>
    {spares
      .filter(t => {
        if (!spareSearch) return true;
        const q = spareSearch.toLowerCase();
        return [t.name, t.model_number, t.serial_number].filter(Boolean).join(' ').toLowerCase().includes(q);
      })
      .map(t => {
        const selected = String(sendForm.tool_id) === String(t.id);
        return (
          <div
            key={t.id}
            onClick={() => sf('tool_id', t.id)}
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid #F0F0F0',
              cursor: 'pointer',
              background: selected ? '#EAF7ED' : 'white',
              borderLeft: selected ? '3px solid var(--green)' : '3px solid transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{
              fontWeight: selected ? 700 : 500,
              fontSize: '13px',
              color: selected ? 'var(--green-dk)' : 'var(--black)',
            }}>
              {t.name}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '2px' }}>
              {[t.model_number, t.serial_number ? 'S/N: ' + t.serial_number : null].filter(Boolean).join(' · ') || 'No model/serial'}
            </div>
          </div>
        );
      })
    }
    {spares.filter(t => {
      if (!spareSearch) return true;
      const q = spareSearch.toLowerCase();
      return [t.name, t.model_number, t.serial_number].filter(Boolean).join(' ').toLowerCase().includes(q);
    }).length === 0 && (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-lt)', fontSize: '13px' }}>
        No tools match your search.
      </div>
    )}
  </div>

  {/* Selected tool confirmation */}
  {sendForm.tool_id && (
    <div style={{
      marginTop: '6px', fontSize: '12px', color: 'var(--green-dk)', fontWeight: 600,
    }}>
      ✓ Selected: {spares.find(t => String(t.id) === String(sendForm.tool_id))?.name}
    </div>
  )}
</div>
                <div className="form-group full">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={sendForm.notes} onChange={e => sf('notes', e.target.value)} placeholder="Reason for sending, any special instructions..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowSendModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSend} disabled={saving}>{saving ? 'Sending...' : 'Send tool'}</button>
            </div>
          </div>
        </div>
      )}
      {/* ── APPROVE REQUEST MODAL ── */}
      {approveModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setApproveModal(null)}>
          <div className="modal" style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <span className="modal-title">Fulfill request — {approveModal.tool_name}</span>
              <button className="close-btn" onClick={() => setApproveModal(null)}>×</button>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--gray-mid)' }}>
              <span>Branch: <strong style={{ color: 'var(--black)' }}>{approveModal.requesting_branch_name}</strong></span>
              <span style={{ marginLeft: '16px' }}>Submitted: <strong style={{ color: 'var(--black)' }}>{new Date(approveModal.submitted_at).toLocaleDateString()}</strong></span>
              {approveModal.notes && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>"{approveModal.notes}"</div>}
            </div>
            <div className="modal-body">
              <div className="form-group full">
                <label className="form-label">Select tool from central spare pool *</label>
                <input
                  className="form-input"
                  placeholder="Search by name, model, serial..."
                  value={approveSearch}
                  onChange={e => setApproveSearch(e.target.value)}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  maxHeight: '260px',
                  overflowY: 'auto',
                  background: 'white',
                }}>
                  {approvePool
                    .filter(t => {
                      const q = approveSearch.toLowerCase();
                      return [t.name, t.model_number, t.serial_number].filter(Boolean).join(' ').toLowerCase().includes(q);
                    })
                    .map(t => {
                      const selected = String(approveToolId) === String(t.id);
                      return (
                        <div
                          key={t.id}
                          onClick={() => setApproveToolId(t.id)}
                          style={{
                            padding: '10px 14px',
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                            background: selected ? '#EAF7ED' : 'white',
                            borderLeft: selected ? '3px solid var(--green)' : '3px solid transparent',
                          }}
                        >
                          <div style={{ fontWeight: selected ? 700 : 500, fontSize: '13px', color: selected ? 'var(--green-dk)' : 'var(--black)' }}>
                            {t.name}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '2px' }}>
                            {[t.model_number, t.serial_number ? 'S/N: ' + t.serial_number : null].filter(Boolean).join(' · ') || 'No model/serial'}
                          </div>
                        </div>
                      );
                    })
                  }
                  {approvePool.filter(t => {
                    const q = approveSearch.toLowerCase();
                    return [t.name, t.model_number, t.serial_number].filter(Boolean).join(' ').toLowerCase().includes(q);
                  }).length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-lt)', fontSize: '13px' }}>
                      {approvePool.length === 0 ? 'No available tools in central spare pool.' : 'No tools match your search.'}
                    </div>
                  )}
                </div>
                {approveToolId && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--green-dk)', fontWeight: 600 }}>
                    ✓ Selected: {approvePool.find(t => String(t.id) === String(approveToolId))?.name}
                    {' — '}{approvePool.find(t => String(t.id) === String(approveToolId))?.model_number || 'no model'}
                    {approvePool.find(t => String(t.id) === String(approveToolId))?.serial_number
                      ? ` · S/N: ${approvePool.find(t => String(t.id) === String(approveToolId))?.serial_number}`
                      : ''}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setApproveModal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={confirmApprove}
                disabled={!approveToolId || approveSaving}
              >
                {approveSaving ? 'Approving...' : 'Approve & assign tool'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── ASSIGN BRANCH SPARE CONFIRM ── */}
      {assignConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAssignConfirm(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Confirm spare assignment</span>
              <button className="close-btn" onClick={() => setAssignConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                Assign <strong>{assignConfirm.spare.name}</strong>
                {assignConfirm.spare.model_number ? ` (${assignConfirm.spare.model_number})` : ''}
                {assignConfirm.spare.serial_number ? ` S/N: ${assignConfirm.spare.serial_number}` : ''} to cover the tagged-out tool at <strong>{assignConfirm.request.requesting_branch_name}</strong>?
              </p>
              <p style={{ fontSize: '12px', color: 'var(--gray-mid)' }}>
                This will remove it from your branch spare inventory. You'll be prompted to request a replenishment from the central pool.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAssignConfirm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => assignBranchSpare(assignConfirm.request, assignConfirm.spare)}>
                Confirm assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REPLENISHMENT PROMPT ── */}
      {replenishPrompt && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Branch spare assigned</span>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                ✓ Branch spare assigned successfully. Your branch spare inventory for <strong>{replenishPrompt.tool_name}</strong> is now empty.
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-mid)' }}>
                Request a replacement branch spare from the central pool?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setReplenishPrompt(null)}>No thanks</button>
              <button className="btn btn-primary" onClick={() => submitReplenishRequest(replenishPrompt)}>
                Yes, request replenishment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NO LINKED TOOL RECORD CONFIRM — Issue #63 ── */}
      {noToolIdConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setNoToolIdConfirm(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">No linked tool record</span>
              <button className="close-btn" onClick={() => setNoToolIdConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                <strong>{noToolIdConfirm.tool_name}</strong> has no linked tool record — this is a legacy direct-send request from before tools were reserved automatically on send.
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-mid)' }}>
                Confirming receipt will create a brand-new tools record. If a tools record for this physical tool already exists elsewhere, this will create a duplicate — check the Fleet page first if you're unsure.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setNoToolIdConfirm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { doConfirmReceipt(noToolIdConfirm); setNoToolIdConfirm(null); }}>
                Create new tool record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── INSPECTIONS PAGE ───────────────────────────────────────
export function InspectionsPage() {
  const { profile, isAdmin, isManager, isExecutive, isTeamMember } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState([]);
  const [filterDept, setFilterDept] = useState([]);
  const [filterStation, setFilterStation] = useState([]);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileInspection, setShowMobileInspection] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = React.useRef(null);
  const inspectionMsg = useMessage();
  const logInspectionBtnRef = React.useRef(null);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

 
  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }
 
  function clearPhoto() {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  }
 
  const load = useCallback(async () => {
    setLoading(true);
    let inspQ = supabase.from('inspections').select('*, branches(name)').order('inspection_date', { ascending: false });
    let toolsQ = supabase.from('tools').select('branch_id, department, station_truck, branches(name)').eq('status', 'active');
    if (!isAdmin && !isExecutive && profile?.branch_id) {
      inspQ = inspQ.eq('branch_id', profile.branch_id);
      toolsQ = toolsQ.eq('branch_id', profile.branch_id);
    }
    if (isManager && profile?.department) {
      inspQ = inspQ.eq('department', profile.department);
      toolsQ = toolsQ.eq('department', profile.department);
    }
    if (isTeamMember) {
      if (profile?.department) {
        inspQ = inspQ.eq('department', profile.department);
        toolsQ = toolsQ.eq('department', profile.department);
      }
      if (profile?.station_truck) {
        inspQ = inspQ.eq('station_truck', profile.station_truck);
        toolsQ = toolsQ.eq('station_truck', profile.station_truck);
      }
    }
    const [{ data: insp }, { data: toolData }, { data: br }] = await Promise.all([
      inspQ, toolsQ, supabase.from('branches').select('*').order('name')
    ]);
    setInspections(insp || []);
    setTools(toolData || []);
    setBranches(br || []);
    setLoading(false);
  }, [isAdmin, isExecutive, isManager, isTeamMember, profile?.branch_id, profile?.department, profile?.station_truck]);
 
  useEffect(() => { load(); }, [load]);
 
  const allStations = [...new Map(
    tools.filter(t => t.station_truck && t.department).map(t => [`${t.branch_id}|${t.department}|${t.station_truck}`, {
      branch_id: t.branch_id,
      branch_name: t.branches?.name || '—',
      department: t.department || '—',
      station_truck: t.station_truck || '—',
    }])
  ).values()].sort((a, b) => a.branch_name.localeCompare(b.branch_name) || a.department.localeCompare(b.department) || a.station_truck.localeCompare(b.station_truck));
 
  const depts = [...new Set(tools.map(t => t.department).filter(Boolean))].sort();
  const stations = [...new Set(tools.map(t => t.station_truck).filter(Boolean))].sort((a, b) => {
    const an = parseInt(a); const bn = parseInt(b);
    return isNaN(an) || isNaN(bn) ? a.localeCompare(b) : an - bn;
  });
 
  const filtered = inspections.filter(i => {
    const br = filterBranch.length === 0 || filterBranch.includes(String(i.branch_id));
    const dp = filterDept.length === 0 || filterDept.includes(i.department);
    const stn = filterStation.length === 0 || filterStation.includes(i.station_truck);
    const mo = !filterMonth || (i.inspection_date && i.inspection_date.slice(0, 7) === filterMonth);
    return br && dp && stn && mo;
  });
 
  const complianceMonth = filterMonth || new Date().toISOString().slice(0, 7);
  const inspectedThisMonth = new Set(
    inspections
      .filter(i => i.inspection_date?.slice(0, 7) === complianceMonth)
      .map(i => `${i.branch_id}|${i.department}|${i.station_truck}`)
  );
 
  const filteredStations = allStations.filter(s => {
    const br = filterBranch.length === 0 || filterBranch.includes(String(s.branch_id));
    const dp = filterDept.length === 0 || filterDept.includes(s.department);
    const stn = filterStation.length === 0 || filterStation.includes(s.station_truck);
    return br && dp && stn;
  });
 
  const compliantCount = filteredStations.filter(s => inspectedThisMonth.has(`${s.branch_id}|${s.department}|${s.station_truck}`)).length;
  const overdueCount = filteredStations.length - compliantCount;
 
  const stationOptions = allStations
    .filter(s => !isAdmin ? String(s.branch_id) === String(profile?.branch_id) : true)
    .map(s => ({ value: `${s.branch_id}|${s.department}|${s.station_truck}`, label: `${s.branch_name} — ${s.department} — ${s.station_truck}` }));
 
  async function saveInspection() {
    if (!form.station_truck?.trim()) { inspectionMsg.showError('Station / truck is required.'); return; }
    setSaving(true);
    let inspPhotoPath = null;
    if (photoFile) {
      setPhotoUploading(true);
      const { uploadPhoto } = await import('../lib/storage');
      const { path, error } = await uploadPhoto(photoFile, 'inspections');
      setPhotoUploading(false);
      if (error) { inspectionMsg.showError('Photo upload failed: ' + error); setSaving(false); return; }
      inspPhotoPath = path;
    }
    const matchingTool = tools.find(t => t.station_truck === form.station_truck && (isAdmin ? String(t.branch_id) === String(form.branch_id) : true));
    await supabase.from('inspections').insert({
      branch_id: form.branch_id || profile?.branch_id,
      department: matchingTool?.department || form.department || null,
      station_truck: form.station_truck,
      inspection_date: form.inspection_date || new Date().toISOString().split('T')[0],
      conducted_by: profile?.id,
      conducted_by_name: profile?.full_name,
      status: 'completed',
      total_tools_checked: parseInt(form.total_tools_checked) || 0,
      discrepancies_found: parseInt(form.discrepancies_found) || 0,
      notes: form.notes || null,
      completed_at: new Date().toISOString(),
      ...(inspPhotoPath && { photo_url: inspPhotoPath }),
    });
    if (parseInt(form.discrepancies_found) > 0) {
      const adminIds = await getAdminIds();
      await sendNotification(adminIds,
            `Inspection discrepancy — ${form.station_truck}`,
            `${profile?.full_name} logged ${form.discrepancies_found} discrepancy(s) on ${form.station_truck}. Review inspection notes.`,
            'inspection_discrepancy',
            'tools',
            'inspect'
          );
      const adminEmails = await getAdminEmails();
      const branchId = form.branch_id || profile?.branch_id;
      const managerEmails = await getBranchManagerEmails(branchId);
      const allEmails = [...new Set([...adminEmails, ...managerEmails])];
      await sendEmail(
        allEmails,
        `Inspection discrepancy — ${form.station_truck}`,
        emailInspectionDiscrepancy(form.station_truck, branches.find(b => String(b.id) === String(branchId))?.name || 'Unknown', form.discrepancies_found, form.notes, profile?.full_name)
      );
    }
    setSaving(false); setShowModal(false); clearPhoto(); load();
  }
 
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
 
  const stationsByBranch = filteredStations.reduce((acc, s) => {
    if (!acc[s.branch_name]) acc[s.branch_name] = [];
    acc[s.branch_name].push(s);
    return acc;
  }, {});
 
  return (
    <div>
      {/* ── PAGE HEADER ── */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <div>
          <div className="page-title">Inspections</div>
          <div className="page-sub">Monthly station and truck inspection compliance</div>
        </div>
        {(isAdmin || isManager || isTeamMember) && (
          <button ref={logInspectionBtnRef} className="btn btn-primary" style={{ flexShrink: 0 }} onClick={() => {
            if (isMobile) {
              setShowMobileInspection(true);
            } else {
              setForm({
                branch_id: profile?.branch_id || '',
                inspection_date: new Date().toISOString().split('T')[0],
                discrepancies_found: '0',
                station_truck: isTeamMember ? profile?.station_truck : '',
              });
              inspectionMsg.clearMessage();
              setShowModal(true);
            }
          }}>+ Log inspection</button>
        )}
      </div>
 
      {/* ── FILTERS ── */}
      <div className="toolbar" style={{ marginBottom: '1rem' }}>
        {isAdmin && (
          <MultiSelect
            label="All branches"
            options={branches.map(b => ({ value: String(b.id), label: b.name }))}
            selected={filterBranch}
            onChange={setFilterBranch}
          />
        )}
        <MultiSelect
          label="All departments"
          options={depts.map(d => ({ value: d, label: d }))}
          selected={filterDept}
          onChange={setFilterDept}
        />
        <MultiSelect
          label="All stations"
          options={stations.map(s => ({ value: s, label: `Station ${s}` }))}
          selected={filterStation}
          onChange={setFilterStation}
        />
        <input
          type="month"
          className="filter-select"
          value={filterMonth}
          onChange={e => setFilterMonth(e.target.value)}
          style={{ minWidth: isMobile ? '100%' : '140px' }}
        />
      </div>
 
      {/* ── COMPLIANCE STAT CARDS ── */}
      {!isTeamMember && (
        <div className="stats-grid" style={{ marginBottom: '1rem' }}>
          <div className="stat-card blue"><div className="stat-label">Total stations</div><div className="stat-value">{filteredStations.length}</div></div>
          <div className="stat-card green"><div className="stat-label">Compliant this month</div><div className="stat-value green">{compliantCount}</div></div>
          <div className="stat-card amber"><div className="stat-label">Overdue</div><div className="stat-value amber">{overdueCount}</div></div>
          <div className="stat-card"><div className="stat-label">Inspections logged</div><div className="stat-value">{filtered.length}</div></div>
        </div>
      )}
 
      {/* ── COMPLIANCE BOARD ── */}
      {!isTeamMember && filteredStations.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <div className="card-title">
              Compliance — {new Date(complianceMonth + '-02').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div className="card-body">
            {Object.entries(stationsByBranch).map(([branchName, branchStations]) => (
              <div key={branchName} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  {branchName}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {branchStations.map(s => {
                    const compliant = inspectedThisMonth.has(`${s.branch_id}|${s.department}|${s.station_truck}`);
                    return (
                      <div key={`${s.branch_id}|${s.department}|${s.station_truck}`} style={{
                        padding: isMobile ? '5px 10px' : '6px 12px',
                        borderRadius: '20px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 600,
                        background: compliant ? '#E8F5E9' : '#FFF3E0',
                        color: compliant ? '#2E7D32' : '#E65100',
                        border: `1px solid ${compliant ? '#C8E6C9' : '#FFE0B2'}`,
                        display: 'flex', alignItems: 'center', gap: '5px',
                      }}>
                        <span>{compliant ? '✓' : '!'}</span>
                        <span>{s.department} — {s.station_truck}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
 
      {/* ── INSPECTION LOG ── */}
      {loading ? (
        <div className="loading"><div className="spinner" />Loading...</div>
      ) : isMobile ? (
        /* Mobile — card list */
        <div>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-mid)', marginBottom: '10px' }}>
            Inspection log
          </div>
          {filtered.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">No inspections found</div>
                <div className="empty-state-sub">Try adjusting your filters.</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map(i => (
                <div key={i.id} className="card" style={{
                  borderLeft: `4px solid ${i.discrepancies_found > 0 ? 'var(--red)' : 'var(--green)'}`,
                }}>
                  <div className="card-body" style={{ padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontFamily: 'var(--font-head)', fontSize: '15px', fontWeight: 700 }}>
                          Station {i.station_truck}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '1px' }}>
                          {[i.branches?.name, i.department].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--black)' }}>
                          {i.inspection_date ? new Date(i.inspection_date).toLocaleDateString() : '—'}
                        </div>
                        {i.discrepancies_found > 0 ? (
                          <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 700, marginTop: '2px' }}>
                            {i.discrepancies_found} issue{i.discrepancies_found !== 1 ? 's' : ''}
                          </div>
                        ) : (
                          <div style={{ fontSize: '11px', color: 'var(--green-dk)', fontWeight: 600, marginTop: '2px' }}>
                            All clear
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-mid)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>{i.total_tools_checked || 0} tools checked</span>
                      <span>By: <strong style={{ color: 'var(--black)' }}>{i.conducted_by_name}</strong></span>
                    </div>
                    {i.notes && (
                      <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '6px', fontStyle: 'italic' }}>
                        "{i.notes}"
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Desktop — full table */
        <div className="card">
          <div className="card-header"><div className="card-title">Inspection log</div></div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Branch</th><th>Dept</th><th>Station / truck</th>
                  <th>Conducted by</th><th>Tools checked</th><th>Discrepancies</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="8">
                    <div className="empty-state">
                      <div className="empty-state-icon">📋</div>
                      <div className="empty-state-title">No inspections found</div>
                      <div className="empty-state-sub">Try adjusting your filters.</div>
                    </div>
                  </td></tr>
                ) : filtered.map(i => (
                  <tr key={i.id} className={i.discrepancies_found > 0 ? 'flagged' : ''}>
                    <td>{i.inspection_date ? new Date(i.inspection_date).toLocaleDateString() : '—'}</td>
                    <td>{i.branches?.name || '—'}</td>
                    <td>{i.department || '—'}</td>
                    <td><strong>{i.station_truck}</strong></td>
                    <td>{i.conducted_by_name}</td>
                    <td>{i.total_tools_checked || 0}</td>
                    <td>{i.discrepancies_found > 0
                      ? <span style={{ color: 'var(--red)', fontWeight: 700 }}>{i.discrepancies_found}</span>
                      : <span style={{ color: 'var(--green-dk)' }}>0</span>}
                    </td>
                    <td className="text-muted text-sm" style={{ maxWidth: '200px' }}>{i.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
 
      {/* ── LOG INSPECTION MODAL (desktop) ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Log inspection</span>
              <button className="close-btn" onClick={() => { setShowModal(false); clearPhoto(); }}>×</button>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
              Conducted by: <strong>{profile?.full_name}</strong>
            </div>
            <div className="modal-body">
             <InlineMessage type={inspectionMsg.type} message={inspectionMsg.message} onDismiss={inspectionMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Inspection date</label>
                  <input className="form-input" type="date" value={form.inspection_date || ''} onChange={e => f('inspection_date', e.target.value)} />
                </div>
                {isAdmin && (
                  <div className="form-group">
                    <label className="form-label">Branch</label>
                    <select className="form-select" value={form.branch_id || ''} onChange={e => f('branch_id', e.target.value)}>
                      <option value="">Select branch</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Station / truck *</label>
                  {isTeamMember ? (
                    <input className="form-input" value={form.station_truck || ''} readOnly style={{ background: 'var(--off-white)', color: 'var(--gray-lt)' }} />
                  ) : (
                    <select className="form-select" value={form._stationKey || ''} onChange={e => {
                      const val = e.target.value;
                      if (val === '__other__') { f('station_truck', '__other__'); f('_stationKey', '__other__'); return; }
                      const [branchId, dept, station] = val.split('|');
                      f('_stationKey', val); f('station_truck', station); f('branch_id', branchId); f('department', dept);
                    }}>
                      <option value="">Select station</option>
                      {stationOptions.map((o, idx) => <option key={`${o.value}-${idx}`} value={o.value}>{o.label}</option>)}
                      <option value="__other__">Other (type below)</option>
                    </select>
                  )}
                </div>
                {form.station_truck === '__other__' && (
                  <div className="form-group">
                    <label className="form-label">Custom station name</label>
                    <input className="form-input" value={form.custom_station || ''} onChange={e => f('custom_station', e.target.value)} placeholder="e.g. Truck 7" />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Total tools checked</label>
                  <input className="form-input" type="number" min="0" value={form.total_tools_checked || ''} onChange={e => f('total_tools_checked', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Discrepancies found</label>
                  <input className="form-input" type="number" min="0" value={form.discrepancies_found || '0'} onChange={e => f('discrepancies_found', e.target.value)} />
                </div>
                <div className="form-group full">
                  <label className="form-label">Notes / discrepancy details</label>
                  <textarea className="form-textarea" value={form.notes || ''} onChange={e => f('notes', e.target.value)} placeholder="Describe any missing or damaged tools..." />
                </div>
                <div className="form-group full">
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                    Photo <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </div>
                  {!photoPreview ? (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                        📷 Take photo
                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoChange} />
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                        📁 Choose file
                        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
                      </label>
                    </div>
                  ) : (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img src={photoPreview} alt="Inspection photo" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }} />
                      <button onClick={clearPhoto} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '4px' }}>{photoFile?.name}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => { setShowModal(false); clearPhoto(); }}>Cancel</button>
              <button className="btn btn-primary" onClick={saveInspection} disabled={saving}>
                {photoUploading ? 'Uploading photo...' : saving ? 'Saving...' : 'Save inspection'}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ── MOBILE INSPECTION FLOW ── */}
      {showMobileInspection && (
        <MobileInspection
          onClose={() => setShowMobileInspection(false)}
          onSuccess={() => { setShowMobileInspection(false); load(); }}
        />
      )}
      {/* ── ONBOARDING CALLOUT ── */}
      <OnboardingCallout
        calloutKey="inspections_log"
        text="Log a monthly inspection for each station or truck. Select the station, enter tools checked, and note any discrepancies. Stations with issues will notify the admin automatically."
        anchorRef={logInspectionBtnRef}
        isMobile={isMobile}
        show={true}
      />
    </div>
  );
}


// ── IMPORT PAGE ────────────────────────────────────────────
export function ImportPage() {
  const { profile } = useAuth();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [step, setStep] = useState(1);
  const [importing, setImporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [defaultBranch, setDefaultBranch] = useState('');
  const [importType, setImportType] = useState('fleet');
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const fileMsg = useMessage();

  const FLEET_FIELDS = [
    { k: 'name', l: 'Tool name *' }, { k: 'model_number', l: 'Model #' },
    { k: 'serial_number', l: 'Serial #' }, { k: 'cost', l: 'Cost ($)' },
    { k: 'department', l: 'Department' }, { k: 'station_truck', l: 'Station/truck' },
    { k: 'description', l: 'Description' },
  ];
  const AUTO = {
    name: ['name', 'tool', 'tool name', 'item', 'tool description', 'description'],
    model_number: ['model', 'model #', 'model number', 'model no'],
    serial_number: ['serial', 'serial #', 'serial number', 'sn'],
    cost: ['cost', 'price', 'value'],
    department: ['dept', 'department', 'division'],
    station_truck: ['station', 'truck', 'station/truck', 'station_truck'],
    description: ['notes', 'details', 'comments']
  };

  useEffect(() => {
    supabase.from('branches').select('*').then(({ data }) => setBranches(data || []));
  }, []);

  // Was `window.XLSX`, populated by a CDN script tag in public/index.html that
  // loaded the whole spreadsheet library on every route in the app. Now loaded
  // on demand from the npm dependency, matching ReceiptsPage.js's existing
  // pattern. Awaited BEFORE the FileReader is wired so the module is certain
  // to be present inside the callback.
  async function handleFile(f) {
    if (!f) return;
    setFileName(f.name);
    let XLSX;
    try {
      XLSX = await import('xlsx');
    } catch (err) {
      // A dynamic import can fail for a reason the old CDN tag could not: a
      // deploy replaces the hashed chunk while a tab is still open, so the
      // chunk this build asks for is gone. Report it instead of doing nothing
      // visible, which is what an unhandled rejection here would look like.
      fileMsg.showError('Could not load the spreadsheet reader. Please refresh and try again.');
      return;
    }
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        if (!json.length) { fileMsg.showError('No data found in file.'); return; }
        const hdrs = Object.keys(json[0]);
        setHeaders(hdrs);
        setRows(json);
        const autoMap = {};
        FLEET_FIELDS.forEach(({ k }) => {
          const aliases = AUTO[k] || [];
          const match = hdrs.find(h => aliases.includes(h.toLowerCase().trim()));
          if (match) autoMap[k] = match;
        });
        setMapping(autoMap);
        setStep(2);
      } catch(err) { fileMsg.showError('Error reading file: ' + err.message); }
    };
    reader.readAsArrayBuffer(f);
  }

  async function doImport() {
    if (!mapping.name) { fileMsg.showError('You must map the Tool name column before importing.'); return; }
    setImporting(true);
    const hqBranch = branches.find(b => b.is_headquarters);
    const targetBranch = defaultBranch || hqBranch?.id;
    const records = rows.map(row => {
      const t = {
        is_spare: importType === 'spare', is_branch_spare: false,
        status: importType === 'spare' ? 'spare' : 'active',
        branch_id: targetBranch ? parseInt(targetBranch) : null,
        entered_by: profile?.id
      };
      FLEET_FIELDS.forEach(({ k }) => {
        if (mapping[k]) t[k] = String(row[mapping[k]] || '').trim() || null;
      });
      return t;
    }).filter(t => t.name);
    const { error } = await supabase.from('tools').insert(records);
    setImporting(false);
    if (error) setResult({ success: false, message: error.message });
    else { setResult({ success: true, message: `Successfully imported ${records.length} tools.` }); setStep(1); setRows([]); setHeaders([]); setFileName(''); }
  }

  return (
    <div>
      <div className="page-header"><div className="page-title">Import data</div><div className="page-sub">Import tools from Excel or CSV files</div></div>
      {result && <div className={`alert ${result.success ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1rem' }}>{result.message}</div>}
      <InlineMessage type={fileMsg.type} message={fileMsg.message} onDismiss={fileMsg.clearMessage} />
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><div className="card-title">Step 1 — Upload file</div></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Import into</label><select className="form-select" value={importType} onChange={e => setImportType(e.target.value)}><option value="fleet">Fleet inventory</option><option value="spare">Spare pool (Dallas HQ)</option></select></div>
            <div className="form-group"><label className="form-label">Default branch</label><select className="form-select" value={defaultBranch} onChange={e => setDefaultBranch(e.target.value)}><option value="">Select branch</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          </div>
          <div style={{ marginTop: '1rem', border: '2px dashed #D0D0D0', borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center', cursor: 'pointer', background: 'var(--off-white)' }} onClick={() => document.getElementById('file-upload').click()}>
            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.4 }}>↑</div>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>{fileName || 'Click to upload'}</div>
            <div style={{ fontSize: '12px', color: 'var(--gray-lt)' }}>Supports .xlsx, .xls, and .csv files</div>
            <input id="file-upload" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          </div>
        </div>
      </div>
      {step >= 2 && headers.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><div className="card-title">Step 2 — Map columns</div></div>
          <div className="card-body">
            <div className="form-grid">
              {FLEET_FIELDS.map(({ k, l }) => (
                <div key={k} className="form-group">
                  <label className="form-label">{l}</label>
                  <select className="form-select" value={mapping[k] || ''} onChange={e => setMapping(p => ({ ...p, [k]: e.target.value }))}>
                    <option value="">— skip —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1.25rem', padding: '1rem', background: 'var(--off-white)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--gray-mid)' }}>
              <strong style={{ color: 'var(--black)' }}>{rows.length} rows</strong> ready to import.
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={doImport} disabled={importing}>{importing ? 'Importing...' : `Import ${rows.length} tools`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ── REPORTS PAGE ──────────────────────────────────────────
export function ReportsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    tools: [], tagouts: [], inspections: [], branches: [], spare_requests: []
  });

  const { profile, isAdmin, isManager, isExecutive } = useAuth();

  useEffect(() => {
    async function loadAll() {
      setLoading(true);

      const isManagerScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
      const branchId = profile?.branch_id;
      const dept = profile?.department;

      let toolsQ = supabase.from('tools').select('*, branches(name), tool_models(msrp)');
      let tagoutsQ = supabase.from('tagout_log').select('*, tools!inner(id, name, model_number, branch_id, department, branches(name), tool_models(msrp))');
      let inspQ = supabase.from('inspections').select('*, branches(name)');

      if (isManagerScoped && branchId) {
        toolsQ = toolsQ.eq('branch_id', branchId);
        tagoutsQ = tagoutsQ.eq('tools.branch_id', branchId);
        inspQ = inspQ.eq('branch_id', branchId);

        if (dept) {
          toolsQ = toolsQ.eq('department', dept);
          tagoutsQ = tagoutsQ.eq('tools.department', dept);
          inspQ = inspQ.eq('department', dept);
        }
      }

      const [tools, tagouts, inspections, branches, requests] = await Promise.all([
        toolsQ,
        tagoutsQ,
        inspQ,
        supabase.from('branches').select('*'),
        supabase.from('spare_requests').select('*'),
      ]);

      setData({
        tools: tools.data || [],
        tagouts: tagouts.data || [],
        inspections: inspections.data || [],
        branches: branches.data || [],
        spare_requests: requests.data || [],
      });
      setLoading(false);
    }
    loadAll();
  }, [isAdmin, isManager, profile?.branch_id, profile?.department]);


  // ── COMPUTED METRICS ──
  const fleetTools = data.tools.filter(t => t.status !== 'retired');
  const totalValue = data.tools
    .filter(t => t.status !== 'retired')
    .reduce((s, t) => s + (resolveToolValue(t) || 0), 0);
  const totalRepairSpend = data.tagouts.reduce((s, t) => s + (parseFloat(t.actual_repair_cost) || 0), 0);
  const repaired = data.tagouts.filter(t => t.repair_or_replace === 'repaired').length;
  const replaced = data.tagouts.filter(t => t.repair_or_replace === 'replaced').length;
  const traded = data.tagouts.filter(t => t.repair_or_replace === 'traded').length;
  const avgRepairDays = (() => {
    const returned = data.tagouts.filter(t => t.tag_date && t.return_date);
    if (!returned.length) return 0;
    const total = returned.reduce((s, t) => {
      const days = (new Date(t.return_date) - new Date(t.tag_date)) / (1000 * 60 * 60 * 24);
      return s + days;
    }, 0);
    return Math.round(total / returned.length);
  })();

  // Tools by branch
  const byBranch = data.branches.map(b => ({
    name: b.name,
    total: data.tools.filter(t => t.branch_id === b.id).length,
    active: data.tools.filter(t => t.branch_id === b.id && t.status === 'active').length,
    tagged: data.tools.filter(t => t.branch_id === b.id && ['tagged_out','in_repair','in_transit'].includes(t.status)).length,
    value: data.tools.filter(t => t.branch_id === b.id && t.status !== 'retired').reduce((s, t) => s + (resolveToolValue(t) || 0), 0),
    repairSpend: data.tagouts.filter(t => t.tools?.branches?.name === b.name).reduce((s, t) => s + (parseFloat(t.actual_repair_cost) || 0), 0),
  })).filter(b => b.total > 0);

  // Most repaired models
  const modelCounts = {};
  data.tagouts.forEach(t => {
    const m = t.tools?.model_number || 'Unknown';
    const name = t.tools?.name || 'Unknown';
    if (!modelCounts[m]) modelCounts[m] = { model: m, name, count: 0, repairSpend: 0 };
    modelCounts[m].count++;
    modelCounts[m].repairSpend += parseFloat(t.actual_repair_cost) || 0;
  });
  const topModels = Object.values(modelCounts).sort((a, b) => b.count - a.count).slice(0, 10);

  // Fleet activity by month
  const monthlyAdded = {};
  const monthlyRetired = {};
  data.tools.forEach(t => {
    if (t.date_added) {
      const m = t.date_added.slice(0, 7);
      monthlyAdded[m] = (monthlyAdded[m] || 0) + 1;
    }
    if (t.status === 'retired' && t.date_added) {
      const m = t.date_added.slice(0, 7);
      monthlyRetired[m] = (monthlyRetired[m] || 0) + 1;
    }
  });

  // Inspection compliance by branch
  const inspCompliance = data.branches.map(b => {
    const bInspections = data.inspections.filter(i => i.branch_id === b.id);
    const thisMonth = bInspections.filter(i => {
      const d = new Date(i.inspection_date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return {
      name: b.name,
      total: bInspections.length,
      thisMonth,
      discrepancies: bInspections.reduce((s, i) => s + (i.discrepancies_found || 0), 0),
      compliant: thisMonth > 0,
    };
  }).filter(b => isManager ? data.branches.find(br => br.id === profile?.branch_id)?.name === b.name : true);

  const TABS = ['overview', 'savings', 'spend', 'health', 'activity', 'inspections'];
const TAB_LABELS = { overview: 'Overview', savings: 'Savings', spend: 'Spend', health: 'Health', activity: 'Activity', inspections: 'Inspections' };

  if (loading) return <div className="loading"><div className="spinner" />Loading reports...</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Reports</div>
        <div className="page-sub">Operational data and cost analysis across all {getCompanyName()} locations</div>
      </div>

      {/* Tab nav — scrollable on mobile */}
<div style={{
  display: 'flex',
  gap: '0',
  marginBottom: '1.5rem',
  borderBottom: '2px solid #E8E8E8',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
}}>
  {TABS.map(tab => (
    <button key={tab} onClick={() => setActiveTab(tab)} style={{
      padding: '10px 14px',
      fontSize: '12px',
      fontWeight: activeTab === tab ? 700 : 500,
      color: activeTab === tab ? 'var(--green-dk)' : 'var(--gray-mid)',
      borderBottom: activeTab === tab ? '2px solid var(--green-dk)' : '2px solid transparent',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      marginBottom: '-2px',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      letterSpacing: '0.3px',
      fontFamily: 'var(--font-head)',
      textTransform: 'uppercase',
    }}>
      {TAB_LABELS[tab]}
    </button>
  ))}
</div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <div>
          <div className="stats-grid">
            <div className="stat-card blue"><div className="stat-label">Total fleet tools</div><div className="stat-value">{fleetTools.length}</div></div>
            <div className="stat-card green"><div className="stat-label">Fleet value</div><div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(totalValue)}</div></div>
            <div className="stat-card amber"><div className="stat-label">Total repair spend</div><div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(totalRepairSpend)}</div></div>
            <div className="stat-card"><div className="stat-label">Avg repair turnaround</div><div className="stat-value">{avgRepairDays} days</div></div>
          </div>
          <div className="stats-grid" style={{ marginTop: '1rem' }}>
            <div className="stat-card green"><div className="stat-label">Total tag-outs</div><div className="stat-value">{data.tagouts.length}</div></div>
            <div className="stat-card"><div className="stat-label">Repaired</div><div className="stat-value green">{repaired}</div></div>
            <div className="stat-card"><div className="stat-label">Replaced</div><div className="stat-value amber">{replaced}</div></div>
            <div className="stat-card"><div className="stat-label">Inspections logged</div><div className="stat-value">{data.inspections.length}</div></div>
          </div>

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><div className="card-title">Fleet by branch</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Branch</th><th>Total tools</th><th>Active</th><th>Out of service</th><th>Fleet value</th><th>Repair spend</th></tr></thead>
                <tbody>
                  {byBranch.map(b => (
                    <tr key={b.name}>
                      <td><strong>{b.name}</strong></td>
                      <td>{b.total}</td>
                      <td><span style={{ color: 'var(--green-dk)', fontWeight: 600 }}>{b.active}</span></td>
                      <td>{b.tagged > 0 ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{b.tagged}</span> : <span style={{ color: 'var(--gray-lt)' }}>0</span>}</td>
                      <td>{fmt$(b.value)}</td>
                      <td>{fmt$(b.repairSpend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── SAVINGS TAB ── */}
      {activeTab === 'savings' && (() => {
        // ── REPAIR / WARRANTY SAVINGS (from tagout_log) ──
        const repairRows = data.tagouts
          .filter(t => t.status === 'Returned')
          .map(t => {
            const savings = calcSavings(t);
            const msrp = (t.repair_or_replace === 'replaced' || t.repair_or_replace === 'traded')
              ? parseFloat(t.replacement_msrp) || null
              : parseFloat(t.tools?.tool_models?.msrp) || null;
            const actual = t.is_warranty ? 0 : (parseFloat(t.actual_repair_cost) ?? null);
            return { ...t, _msrp: msrp, _actual: actual, _savings: savings };
          });
 
        const repairWithSavings = repairRows.filter(r => r._savings != null);
        const totalRepairSpend = repairWithSavings.reduce((s, r) => s + (r._actual || 0), 0);
        const warrantyRepairCount = repairRows.filter(r => r.is_warranty).length;
        const totalRepairSavings = repairWithSavings.reduce((s, r) => s + (r._savings || 0), 0);
 
        // ── ACQUISITION SAVINGS (from tools table) ──
        const acqRows = data.tools
          .filter(t => t.status !== 'retired')
          .map(t => {
            const savings = calcAcquisitionSavings(t);
            const msrp = parseFloat(t.tool_models?.msrp) || null;
            const cost = parseFloat(t.cost) || null;
            return { ...t, _msrp: msrp, _cost: cost, _savings: savings };
          });
 
        const acqWithSavings = acqRows.filter(r => r._savings != null && r._savings > 0);
        const totalAcqSavings = acqWithSavings.reduce((s, r) => s + r._savings, 0);
        const acqMissingData = data.tools.filter(t =>
          t.status !== 'retired' &&
          (t.acquisition_type === 'unknown' || !t.acquisition_type) &&
          t.tool_models?.msrp
        ).length;
 
        // ── COMBINED TOTAL ──
        const grandTotalSavings = (totalRepairSavings || 0) + (totalAcqSavings || 0);
 
        // ── SAVINGS BY BRANCH ──
        const savingsByBranch = data.branches.map(b => {
          const bRepair = repairWithSavings.filter(r => r.tools?.branches?.name === b.name);
          const bAcq = acqWithSavings.filter(r => r.branch_id === b.id);
          const repairSavings = bRepair.reduce((s, r) => s + r._savings, 0);
          const acqSavings = bAcq.reduce((s, r) => s + r._savings, 0);
          return {
            name: b.name,
            repairCount: bRepair.length,
            repairSavings,
            acqCount: bAcq.length,
            acqSavings,
            total: repairSavings + acqSavings,
          };
        }).filter(b => b.repairSavings > 0 || b.acqSavings > 0);
 
        return (
          <div>
            {/* ── COMBINED TOTAL BANNER ── */}
            <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid var(--green-dk)', background: '#F1FFF3' }}>
              <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total savings — all sources</div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--green-dk)', fontFamily: 'var(--font-head)' }}>{fmt$(grandTotalSavings)}</div>
                </div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Repair &amp; warranty</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#2E7D32' }}>{fmt$(totalRepairSavings)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Acquisition</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1565C0' }}>{fmt$(totalAcqSavings)}</div>
                  </div>
                </div>
              </div>
            </div>
 
            {/* ── REPAIR SAVINGS STAT CARDS ── */}
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Repair &amp; warranty savings</div>
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="stat-card green">
                <div className="stat-label">Repair savings</div>
                <div className="stat-value green" style={{ fontSize: '20px' }}>{fmt$(totalRepairSavings || null)}</div>
              </div>
              <div className="stat-card amber">
              <div className="stat-label">Total repair spend</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(totalRepairSpend)}</div>
            </div>
              <div className="stat-card">
                <div className="stat-label">Repairs tracked</div>
                <div className="stat-value">{repairWithSavings.length}</div>
              </div>
              <div className="stat-card blue">
                <div className="stat-label">Warranty claims</div>
                <div className="stat-value" style={{ color: '#1565C0' }}>{warrantyRepairCount}</div>
              </div>
            </div>
 
            {/* ── ACQUISITION SAVINGS STAT CARDS ── */}
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Acquisition savings</div>
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="stat-card blue">
                <div className="stat-label">Acquisition savings</div>
                <div className="stat-value" style={{ fontSize: '20px', color: '#1565C0' }}>{fmt$(totalAcqSavings || null)}</div>
              </div>
              <div className="stat-card green">
                <div className="stat-label">Tools with savings data</div>
                <div className="stat-value green">{acqWithSavings.length}</div>
              </div>
              <div className="stat-card amber">
                <div className="stat-label">Missing acquisition type</div>
                <div className="stat-value amber">{acqMissingData}</div>
              </div>
            </div>
 
            {/* ── SAVINGS BY BRANCH ── */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-header"><div className="card-title">Savings by branch</div></div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Branch</th><th>Repair savings</th><th>Acquisition savings</th><th>Total savings</th></tr>
                  </thead>
                  <tbody>
                    {savingsByBranch.length === 0 ? (
                      <tr><td colSpan="4">
                        <div className="empty-state">
                          <div className="empty-state-icon">💰</div>
                          <div className="empty-state-title">No savings data yet</div>
                          <div className="empty-state-sub">Savings appear as tools are returned to service and acquisition types are set.</div>
                        </div>
                      </td></tr>
                    ) : savingsByBranch.map(b => (
                      <tr key={b.name}>
                        <td><strong>{b.name}</strong></td>
                        <td>{fmt$(b.repairSavings || null)}</td>
                        <td>{fmt$(b.acqSavings || null)}</td>
                        <td><strong style={{ color: 'var(--green-dk)' }}>{fmt$(b.total || null)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
 
            {/* ── REPAIR LINE ITEMS ── */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-header"><div className="card-title">Repair &amp; warranty — line items</div></div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Tool</th><th>Branch</th><th>Date</th><th>Outcome</th><th>MSRP</th><th>Actual cost</th><th>Savings</th></tr>
                  </thead>
                  <tbody>
                    {repairRows.length === 0 ? (
                      <tr><td colSpan="7"><div className="empty-state"><div className="empty-state-icon">🔧</div><div className="empty-state-title">No repair data yet</div></div></td></tr>
                    ) : repairRows.sort((a, b) => (b._savings || 0) - (a._savings || 0)).map(r => (
                      <tr key={r.id}>
                        <td><strong>{r.tools?.name || '—'}</strong></td>
                        <td>{r.tools?.branches?.name || '—'}</td>
                        <td className="text-muted">{r.return_date ? new Date(r.return_date).toLocaleDateString() : '—'}</td>
                        <td><span className={`badge ${r.repair_or_replace === 'repaired' ? 'badge-active' : r.repair_or_replace === 'traded' ? 'badge-transit' : 'badge-pending'}`}>{r.repair_or_replace || '—'}</span></td>
                        <td>{fmt$(r._msrp)}</td>
                        <td>{r.is_warranty ? <span style={{ color: '#1565C0', fontWeight: 600 }}>$0.00 (warranty)</span> : fmt$(r._actual)}</td>
                        <td>
                          <strong style={{ color: r._savings > 0 ? 'var(--green-dk)' : r._savings < 0 ? 'var(--red)' : 'var(--gray-mid)' }}>
                            {fmt$(r._savings)}
                          </strong>
                          {r.is_warranty && <span className="badge" style={{ marginLeft: '6px', fontSize: '10px', background: '#E8F5E9', color: '#2E7D32' }}>WARRANTY</span>}
                          {r._savings == null && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', fontStyle: 'italic', marginLeft: '4px' }}>no MSRP</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
 
            {/* ── ACQUISITION LINE ITEMS ── */}
            <div className="card">
              <div className="card-header"><div className="card-title">Acquisition savings — line items</div></div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Tool</th><th>Branch</th><th>How acquired</th><th>MSRP</th><th>Cost paid</th><th>Savings</th></tr>
                  </thead>
                  <tbody>
                    {acqWithSavings.length === 0 ? (
                      <tr><td colSpan="6">
                        <div className="empty-state">
                          <div className="empty-state-icon">🏷️</div>
                          <div className="empty-state-title">No acquisition savings yet</div>
                          <div className="empty-state-sub">Set acquisition type on tools in Fleet Inventory to track below-retail savings.</div>
                        </div>
                      </td></tr>
                    ) : acqWithSavings.sort((a, b) => (b._savings || 0) - (a._savings || 0)).map(t => (
                      <tr key={t.id}>
                        <td><strong>{t.name}</strong></td>
                        <td>{t.branches?.name || '—'}</td>
                        <td>
                          <span style={{ fontSize: '12px', color: 'var(--gray-mid)' }}>
                            {t.acquisition_type === 'repair_shop' ? 'Repair shop' :
                             t.acquisition_type === 'purchased_sale' ? 'On sale' :
                             t.acquisition_type === 'warranty' ? 'Warranty' : '—'}
                          </span>
                        </td>
                        <td>{fmt$(t._msrp)}</td>
                        <td>{t.acquisition_type === 'warranty' ? <span style={{ color: '#1565C0', fontWeight: 600 }}>$0.00</span> : fmt$(t._cost)}</td>
                        <td><strong style={{ color: 'var(--green-dk)' }}>{fmt$(t._savings)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SPEND & COSTS TAB ── */}
      {activeTab === 'spend' && (() => {
        // ── FORMAL REPAIR SPEND (from tagout_log) ──
        const repairTagouts = data.tagouts.filter(t => t.actual_repair_cost != null && !t.is_warranty);
        const formalRepairSpend = repairTagouts.reduce((s, t) => s + (parseFloat(t.actual_repair_cost) || 0), 0);
        const warrantyCount = data.tagouts.filter(t => t.is_warranty).length;
        const avgRepairCost = repairTagouts.length > 0 ? formalRepairSpend / repairTagouts.length : null;
 
        // ── ACQUISITION SPEND (from tools table) ──
        const acqSpendTools = data.tools.filter(t =>
          t.status !== 'retired' &&
          t.cost != null &&
          parseFloat(t.cost) > 0 &&
          (t.acquisition_type === 'repair_shop' || t.acquisition_type === 'purchased_sale' || t.acquisition_type === 'purchased_new')
        );
        const totalAcqSpend = acqSpendTools.reduce((s, t) => s + (parseFloat(t.cost) || 0), 0);
        const missingCostData = data.tools.filter(t =>
          t.status !== 'retired' &&
          (t.acquisition_type === 'unknown' || !t.acquisition_type) &&
          (!t.cost || parseFloat(t.cost) === 0)
        ).length;
 
        // ── COMBINED TOTAL ──
        const grandTotalSpend = formalRepairSpend + totalAcqSpend;
 
        // ── ACQUISITION SPEND BY BRANCH ──
        const acqSpendByBranch = data.branches.map(b => {
          const bTools = acqSpendTools.filter(t => t.branch_id === b.id);
          const bSpend = bTools.reduce((s, t) => s + (parseFloat(t.cost) || 0), 0);
          return { name: b.name, count: bTools.length, spend: bSpend };
        }).filter(b => b.spend > 0);
 
        return (
          <div>
            {/* ── COMBINED TOTAL BANNER ── */}
            <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #E65100', background: '#FFF8F0' }}>
              <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total spend — all sources</div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#E65100', fontFamily: 'var(--font-head)' }}>{fmt$(grandTotalSpend)}</div>
                </div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Formal repairs</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#C62828' }}>{fmt$(formalRepairSpend)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Acquisitions</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#E65100' }}>{fmt$(totalAcqSpend)}</div>
                  </div>
                </div>
              </div>
            </div>
 
            {/* ── FORMAL REPAIR SPEND STAT CARDS ── */}
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Formal repair spend</div>
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="stat-card amber">
              <div className="stat-label">Total repair spend</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(formalRepairSpend)}</div>
            </div>
              <div className="stat-card">
                <div className="stat-label">Avg cost per repair</div>
                <div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(avgRepairCost)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Repair vs replace</div>
                <div className="stat-value" style={{ fontSize: '16px' }}>{repaired} repaired · {replaced} replaced · {traded} traded</div>
              </div>
              <div className="stat-card blue">
                <div className="stat-label">Warranty claims</div>
                <div className="stat-value" style={{ color: '#1565C0' }}>{warrantyCount}</div>
              </div>
            </div>
 
            {/* ── ACQUISITION SPEND STAT CARDS ── */}
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Acquisition spend</div>
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="stat-card amber">
                <div className="stat-label">Acquisition spend</div>
                <div className="stat-value" style={{ fontSize: '20px' }}>{fmt$(totalAcqSpend)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Tools with cost data</div>
                <div className="stat-value">{acqSpendTools.length}</div>
              </div>
              <div className="stat-card amber">
                <div className="stat-label">Missing cost data</div>
                <div className="stat-value amber">{missingCostData}</div>
              </div>
              <div className="stat-card green">
                <div className="stat-label">Spare requests fulfilled</div>
                <div className="stat-value green">{data.spare_requests.filter(r => r.status === 'fulfilled').length}</div>
              </div>
            </div>
 
            {/* ── REPAIR SPEND BY BRANCH ── */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-header"><div className="card-title">Repair spend by branch</div></div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Branch</th><th>Tag-outs</th><th>Repaired</th><th>Replaced</th><th>Warranty</th><th>Repair spend</th><th>Acquisition spend</th><th>Total spend</th></tr>
                  </thead>
                  <tbody>
                    {byBranch.map(b => {
                      const bTagouts = data.tagouts.filter(t => t.tools?.branches?.name === b.name);
                      const bRepairSpend = bTagouts.reduce((s, t) => s + (parseFloat(t.actual_repair_cost) || 0), 0);
                      const bAcqSpend = acqSpendTools.filter(t => t.branch_id === b.id).reduce((s, t) => s + (parseFloat(t.cost) || 0), 0);
                      return (
                        <tr key={b.name}>
                          <td><strong>{b.name}</strong></td>
                          <td>{bTagouts.length}</td>
                          <td>{bTagouts.filter(t => t.repair_or_replace === 'repaired').length}</td>
                          <td>{bTagouts.filter(t => t.repair_or_replace === 'replaced').length}</td>
                          <td>{bTagouts.filter(t => t.is_warranty).length}</td>
                          <td>{fmt$(bRepairSpend)}</td>
                          <td>{fmt$(bAcqSpend)}</td>
                          <td><strong>{fmt$(bRepairSpend + bAcqSpend)}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
 
            {/* ── RECENT REPAIR COSTS ── */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-header"><div className="card-title">Recent repair costs</div></div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Tool</th><th>Model</th><th>Branch</th><th>Outcome</th><th>Actual cost</th><th>Return date</th></tr></thead>
                  <tbody>
                    {data.tagouts.filter(t => t.actual_repair_cost && t.return_date).slice(0, 20).map(t => (
                      <tr key={t.id}>
                        <td><strong>{t.tools?.name || '—'}</strong></td>
                        <td className="text-muted">{t.tools?.model_number || '—'}</td>
                        <td>{t.tools?.branches?.name || '—'}</td>
                        <td><span className={`badge ${t.repair_or_replace === 'repaired' ? 'badge-active' : t.repair_or_replace === 'traded' ? 'badge-transit' : 'badge-pending'}`}>{t.repair_or_replace || '—'}</span></td>
                        <td>{t.is_warranty ? <span style={{ color: '#1565C0', fontWeight: 600 }}>$0.00 (warranty)</span> : fmt$(t.actual_repair_cost)}</td>
                        <td className="text-muted">{t.return_date ? new Date(t.return_date).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                    {data.tagouts.filter(t => t.actual_repair_cost && t.return_date).length === 0 && (
                      <tr><td colSpan="6"><div className="empty-state"><div className="empty-state-icon">💰</div><div className="empty-state-title">No repair cost data yet</div><div className="empty-state-sub">Costs appear when tools are returned to service with actual repair costs entered.</div></div></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
 
            {/* ── ACQUISITION COST LINE ITEMS ── */}
            <div className="card">
              <div className="card-header"><div className="card-title">Acquisition costs — line items</div></div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Tool</th><th>Branch</th><th>How acquired</th><th>MSRP</th><th>Cost paid</th></tr>
                  </thead>
                  <tbody>
                    {acqSpendTools.length === 0 ? (
                      <tr><td colSpan="5">
                        <div className="empty-state">
                          <div className="empty-state-icon">🏷️</div>
                          <div className="empty-state-title">No acquisition cost data yet</div>
                          <div className="empty-state-sub">Set acquisition type and cost on tools in Fleet Inventory to track here.</div>
                        </div>
                      </td></tr>
                    ) : acqSpendTools.sort((a, b) => (parseFloat(b.cost) || 0) - (parseFloat(a.cost) || 0)).map(t => (
                      <tr key={t.id}>
                        <td><strong>{t.name}</strong></td>
                        <td>{t.branches?.name || '—'}</td>
                        <td>
                          <span style={{ fontSize: '12px', color: 'var(--gray-mid)' }}>
                            {t.acquisition_type === 'repair_shop' ? 'Repair shop' :
                             t.acquisition_type === 'purchased_sale' ? 'On sale' :
                             t.acquisition_type === 'purchased_new' ? 'Purchased new' : '—'}
                          </span>
                        </td>
                        <td>{t.tool_models?.msrp ? fmt$(t.tool_models.msrp) : '—'}</td>
                        <td><strong>{fmt$(t.cost)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── TOOL HEALTH TAB ── */}
      {activeTab === 'health' && (
        <div>
          <div className="stats-grid">
            <div className="stat-card amber"><div className="stat-label">Total tag-outs</div><div className="stat-value">{data.tagouts.length}</div></div>
            <div className="stat-card green"><div className="stat-label">Repaired</div><div className="stat-value green">{repaired}</div></div>
            <div className="stat-card"><div className="stat-label">Replaced</div><div className="stat-value amber">{replaced}</div></div>
            <div className="stat-card blue"><div className="stat-label">Avg turnaround</div><div className="stat-value">{avgRepairDays} days</div></div>
          </div>

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><div className="card-title">Most repaired tool models</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Model #</th><th>Tool name</th><th>Tag-outs</th><th>Total repair spend</th></tr></thead>
                <tbody>
                  {topModels.length === 0 ? (
                    <tr><td colSpan="4"><div className="empty-state"><div className="empty-state-icon">🔧</div><div className="empty-state-title">No repair data yet</div><div className="empty-state-sub">Data appears as tools are tagged out and returned to service.</div></div></td></tr>
                  ) : topModels.map(m => (
                    <tr key={m.model}>
                      <td><strong>{m.model}</strong></td>
                      <td>{m.name}</td>
                      <td>{m.count}</td>
                      <td>{fmt$(m.repairSpend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="card-header"><div className="card-title">Tag-out reasons breakdown</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Reason</th><th>Count</th><th>% of total</th></tr></thead>
                <tbody>
                  {(() => {
                    const reasons = {};
                    data.tagouts.forEach(t => { reasons[t.reason] = (reasons[t.reason] || 0) + 1; });
                    return Object.entries(reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                      <tr key={reason}>
                        <td><strong>{reason}</strong></td>
                        <td>{count}</td>
                        <td>{data.tagouts.length > 0 ? Math.round(count / data.tagouts.length * 100) : 0}%</td>
                      </tr>
                    ));
                  })()}
                  {data.tagouts.length === 0 && <tr><td colSpan="3"><div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-title">No data yet</div></div></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── FLEET ACTIVITY TAB ── */}
      {activeTab === 'activity' && (
        <div>
          <div className="stats-grid">
            <div className="stat-card blue"><div className="stat-label">Total tools ever added</div><div className="stat-value">{data.tools.length}</div></div>
            <div className="stat-card green"><div className="stat-label">Currently active</div><div className="stat-value green">{data.tools.filter(t => t.status === 'active').length}</div></div>
            <div className="stat-card amber"><div className="stat-label">Retired</div><div className="stat-value amber">{data.tools.filter(t => t.status === 'retired').length}</div></div>
            <div className="stat-card"><div className="stat-label">Pending receipt</div><div className="stat-value">{data.tools.filter(t => t.status === 'pending').length}</div></div>
          </div>

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><div className="card-title">Tools added by month</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Month</th><th>Tools added</th></tr></thead>
                <tbody>
                  {Object.entries(monthlyAdded).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 12).map(([month, count]) => (
                    <tr key={month}>
                      <td>{new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</td>
                      <td>{count}</td>
                    </tr>
                  ))}
                  {Object.keys(monthlyAdded).length === 0 && <tr><td colSpan="2"><div className="empty-state"><div className="empty-state-icon">📈</div><div className="empty-state-title">No activity data yet</div></div></td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="card-header"><div className="card-title">Tool lifecycle — highest repair spend</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Tool</th><th>Model</th><th>Branch</th><th>Status</th><th>Purchase cost</th><th>Repair spend</th><th>Total cost</th></tr></thead>
                <tbody>
                  {(() => {
                    const toolSpend = {};
                    data.tagouts.forEach(t => {
                      if (!t.tool_id) return;
                      if (!toolSpend[t.tool_id]) toolSpend[t.tool_id] = 0;
                      toolSpend[t.tool_id] += parseFloat(t.actual_repair_cost) || 0;
                    });
                    return data.tools
                      .filter(t => toolSpend[t.id] > 0)
                      .sort((a, b) => (toolSpend[b.id] || 0) - (toolSpend[a.id] || 0))
                      .slice(0, 15)
                      .map(t => (
                        <tr key={t.id}>
                          <td><strong>{t.name}</strong></td>
                          <td className="text-muted">{t.model_number || '—'}</td>
                          <td>{t.branches?.name || '—'}</td>
                          <td><span className={`badge ${STATUS_BADGE[t.status] || 'badge-active'}`}>{t.status}</span></td>
                          <td>{fmt$(t.cost)}</td>
                          <td>{fmt$(toolSpend[t.id])}</td>
                          <td><strong>{fmt$((parseFloat(t.cost) || 0) + (toolSpend[t.id] || 0))}</strong></td>
                        </tr>
                      ));
                  })()}
                  {data.tools.filter(t => data.tagouts.some(tg => tg.tool_id === t.id && tg.actual_repair_cost)).length === 0 && <tr><td colSpan="7"><div className="empty-state"><div className="empty-state-icon">🔄</div><div className="empty-state-title">No lifecycle data yet</div><div className="empty-state-sub">Appears when tools have been repaired with costs entered.</div></div></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── INSPECTIONS TAB ── */}
      {activeTab === 'inspections' && (
        <div>
          <div className="stats-grid">
            <div className="stat-card blue"><div className="stat-label">Total inspections</div><div className="stat-value">{data.inspections.length}</div></div>
            <div className="stat-card green"><div className="stat-label">This month</div><div className="stat-value green">{data.inspections.filter(i => { const d = new Date(i.inspection_date); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length}</div></div>
            <div className="stat-card amber"><div className="stat-label">With discrepancies</div><div className="stat-value amber">{data.inspections.filter(i => i.discrepancies_found > 0).length}</div></div>
            <div className="stat-card"><div className="stat-label">Total discrepancies</div><div className="stat-value">{data.inspections.reduce((s, i) => s + (i.discrepancies_found || 0), 0)}</div></div>
          </div>

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><div className="card-title">Compliance by branch — this month</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Branch</th><th>This month</th><th>Total inspections</th><th>Discrepancies found</th><th>Status</th></tr></thead>
                <tbody>
                  {inspCompliance.map(b => (
                    <tr key={b.name}>
                      <td><strong>{b.name}</strong></td>
                      <td>{b.thisMonth}</td>
                      <td>{b.total}</td>
                      <td>{b.discrepancies > 0 ? <span style={{ color: 'var(--red)', fontWeight: 700 }}>{b.discrepancies}</span> : 0}</td>
                      <td>{b.compliant ? <span className="badge badge-active">Compliant</span> : <span className="badge badge-tagged">No inspection this month</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="card-header"><div className="card-title">Inspection history</div></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Branch</th><th>Station / truck</th><th>Conducted by</th><th>Tools checked</th><th>Discrepancies</th></tr></thead>
                <tbody>
                  {data.inspections.length === 0 ? (
                    <tr><td colSpan="6"><div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No inspections logged yet</div></div></td></tr>
                  ) : data.inspections.slice(0, 20).map(i => (
                    <tr key={i.id} className={i.discrepancies_found > 0 ? 'flagged' : ''}>
                      <td>{i.inspection_date ? new Date(i.inspection_date).toLocaleDateString() : '—'}</td>
                      <td>{i.branches?.name || '—'}</td>
                      <td><strong>{i.station_truck}</strong></td>
                      <td>{i.conducted_by_name}</td>
                      <td>{i.total_tools_checked || 0}</td>
                      <td>{i.discrepancies_found > 0 ? <span style={{ color: 'var(--red)', fontWeight: 700 }}>{i.discrepancies_found}</span> : <span style={{ color: 'var(--green-dk)' }}>0</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ── MODEL MANAGEMENT PAGE ─────────────────────────────────
export function ModelsPage() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { id, msrp }
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadModels(); }, []);

  async function loadModels() {
    setLoading(true);
    const { data } = await supabase
      .from('tool_models')
      .select('*')
      .order('model_number');
    setModels(data || []);
    setLoading(false);
  }

  async function saveMsrp(id) {
    setSaving(true);
    await supabase
      .from('tool_models')
      .update({ msrp: editing.msrp === '' ? null : parseFloat(editing.msrp) })
      .eq('id', id);
    setSaving(false);
    setEditing(null);
    loadModels();
  }

  const filtered = models.filter(m =>
    m.model_number?.toLowerCase().includes(search.toLowerCase()) ||
    m.tool_name?.toLowerCase().includes(search.toLowerCase())
  );

  const withMsrp = models.filter(m => m.msrp != null).length;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Model Management</h1>
          <p className="page-subtitle">Set MSRP by model number for savings tracking</p>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="stat-label">Total models</div>
          <div className="stat-value">{models.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">MSRP set</div>
          <div className="stat-value" style={{ color: '#2E7D32' }}>{withMsrp}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Missing MSRP</div>
          <div className="stat-value" style={{ color: '#E65100' }}>{models.length - withMsrp}</div>
        </div>
      </div>

      <div className="toolbar">
        <input
          className="search-input"
          placeholder="Search model # or name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="empty-state">Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Model #</th>
              <th>Tool name</th>
              <th>MSRP</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.id}>
                <td><strong>{m.model_number}</strong></td>
                <td>{m.tool_name || '—'}</td>
                <td>
                  {editing?.id === m.id ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      style={{ width: '100px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                      value={editing.msrp}
                      onChange={e => setEditing({ ...editing, msrp: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') saveMsrp(m.id); if (e.key === 'Escape') setEditing(null); }}
                      autoFocus
                    />
                  ) : (
                    m.msrp != null ? `$${parseFloat(m.msrp).toFixed(2)}` : <span style={{ color: '#9A9A9A' }}>Not set</span>
                  )}
                </td>
                <td>
                  {editing?.id === m.id ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => saveMsrp(m.id)} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn btn-sm" onClick={() => setEditing({ id: m.id, msrp: m.msrp ?? '' })}>
                      {m.msrp != null ? 'Edit' : 'Set MSRP'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}